import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";

const VERIFICATION_METHODS = [
    { id: "in_person", label: "En personne", icon: "bg-green-400" },
    { id: "phone", label: "Appel téléphonique", icon: "bg-blue-400" },
    { id: "alt_email", label: "E-mail alternatif", icon: "bg-orange-400" },
    { id: "teams", label: "Microsoft Teams", icon: "bg-purple-400" },
];

const TRANSMISSION_METHODS = [
    { id: "in_person", label: "En personne", icon: "bg-green-400" },
    { id: "phone", label: "Par téléphone", icon: "bg-blue-400" },
    { id: "encrypted_email", label: "E-mail chiffré", icon: "bg-amber-400" },
];

const ISSUE_TYPES = [
    { id: "forgotten", label: "Mot de passe oublié", icon: "bg-yellow-400" },
    { id: "locked", label: "Compte verrouillé", icon: "bg-red-500" },
];

function OptionWithDot({ icon, label }) {
    return (
        <span className="flex items-center gap-2.5">
            <span className={`w-2.5 h-2.5 rounded-full ${icon}`} />
            {label}
        </span>
    );
}

export function PasswordResetForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("medium");
    const [accountName, setAccountName] = useState("");
    const [issueType, setIssueType] = useState("forgotten");
    const [verificationMethod, setVerificationMethod] = useState("in_person");
    const [transmissionMethod, setTransmissionMethod] = useState("in_person");

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            title: baseForm.title || `Réinitialisation mot de passe — ${accountName}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                account_name: accountName,
                issue_type: issueType,
                verification_method: verificationMethod,
                transmission_method: transmissionMethod,
            },
        });
    };

    const currentIssue = ISSUE_TYPES.find((t) => t.id === issueType);
    const currentVerif = VERIFICATION_METHODS.find((m) => m.id === verificationMethod);
    const currentTransmit = TRANSMISSION_METHODS.find((m) => m.id === transmissionMethod);

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Réinitialisation mot de passe — jdupont"
                descriptionPlaceholder="Contexte : qui a appelé, urgence, remarques..."
            >
                {/* Account name */}
                <div className="form-group">
                    <Label className="form-label">Compte AD concerné (sAMAccountName ou e-mail)</Label>
                    <Input
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        placeholder="Ex: jdupont ou jean.dupont@eggtelsa.ch"
                        data-testid="account-name"
                    />
                </div>

                {/* Issue type */}
                <div className="form-group">
                    <Label className="form-label">Type de problème</Label>
                    <Select value={issueType} onValueChange={setIssueType}>
                        <SelectTrigger data-testid="issue-type">
                            <SelectValue>
                                {currentIssue && <OptionWithDot icon={currentIssue.icon} label={currentIssue.label} />}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {ISSUE_TYPES.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                    <OptionWithDot icon={t.icon} label={t.label} />
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Verification method */}
                <div className="form-group">
                    <Label className="form-label">Méthode de vérification d'identité</Label>
                    <Select value={verificationMethod} onValueChange={setVerificationMethod}>
                        <SelectTrigger data-testid="verification-method">
                            <SelectValue>
                                {currentVerif && <OptionWithDot icon={currentVerif.icon} label={currentVerif.label} />}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {VERIFICATION_METHODS.map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                    <OptionWithDot icon={m.icon} label={m.label} />
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Transmission method */}
                <div className="form-group">
                    <Label className="form-label">Nouveau mot de passe transmis comment</Label>
                    <Select value={transmissionMethod} onValueChange={setTransmissionMethod}>
                        <SelectTrigger data-testid="transmission-method">
                            <SelectValue>
                                {currentTransmit && <OptionWithDot icon={currentTransmit.icon} label={currentTransmit.label} />}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {TRANSMISSION_METHODS.map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                    <OptionWithDot icon={m.icon} label={m.label} />
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
